import { useMemo, type ReactNode } from 'react'
import { FormatIcu } from '@tolgee/format-icu'
import { BackendFetch, LanguageStorage, Tolgee, TolgeeProvider as TolgeeBaseProvider } from '@tolgee/react'

import { DEFAULT_LOCATION_LANGUAGE, LOCATION_LANGUAGES } from './consts'
import type { LocationLanguage } from './types'

interface I18nProviderProps {
  children: ReactNode
  apiKey?: string
  apiUrl?: string
  /** CDN path for dynamic translation loading */
  filesUrlPrefix?: string
  loadingFallback?: ReactNode
  defaultLanguage?: LocationLanguage
}

export const I18nProvider = ({
  children,
  apiKey,
  apiUrl,
  filesUrlPrefix = '/i18n/',
  loadingFallback,
  // v6 requires an explicit default (v4 silently fell back to 'en' despite this package's own
  // README always documenting 'pt-BR' as the default) — matching the documented behavior here.
  defaultLanguage = DEFAULT_LOCATION_LANGUAGE,
}: I18nProviderProps) => {
  // @tolgee/react v6 replaced the declarative apiKey/apiUrl/filesUrlPrefix/defaultLanguage props
  // with a Tolgee() instance built from plugins. BackendFetch replicates the old filesUrlPrefix
  // static-file loading (same default path shape: `${prefix}/${language}.json`). LanguageStorage
  // replicates persistence to localStorage (`__tolgee_currentLanguage`), which v4 did automatically
  // without any plugin. FormatIcu replicates the ICU MessageFormat interpolation ({param},
  // {count, plural, ...}, {type, select, ...}) that v4's core did automatically — v6 requires a
  // formatter plugin explicitly, and this repo's translations use full ICU plural/select syntax
  // (not just plain {param}), so FormatSimple alone isn't enough.
  const tolgee = useMemo(
    () =>
      Tolgee()
        .use(BackendFetch({ prefix: filesUrlPrefix }))
        .use(LanguageStorage())
        .use(FormatIcu())
        .init({ apiKey, apiUrl, defaultLanguage, availableLanguages: LOCATION_LANGUAGES }),
    [apiKey, apiUrl, filesUrlPrefix, defaultLanguage]
  )

  return (
    <TolgeeBaseProvider tolgee={tolgee} fallback={loadingFallback}>
      {children}
    </TolgeeBaseProvider>
  )
}
